import { useState, useEffect, useRef, useCallback } from "react";
import { FormikProps } from "formik";
import { useDispatch } from "react-redux";
import {
  DenmarkPublish as DenmarkPublishInterface,
  MediaModel,
  HostSubSection,
} from "@/database";
import { setHostSubsection } from "@/store/media/media.slice";
import { deleteDenmarkPublish } from "@/store/media/media.action";
import { Host } from "@/store/media/media.models";
import { defaultTeamModel } from "@/database";
import type { MediaDetailsForm } from "../../../routes/pages/MediaDetail/MediaDetailsForm";
import { DENMARK_STATUS } from "@/constants";

/** Smallest ct (epoch) = oldest; missing/invalid ct is treated as newest for comparison. */
function pickOldestDenmarkSocialByCt<T extends { ct?: unknown }>(
  items: T[]
): T | undefined {
  if (items.length === 0) return undefined;
  const ctNum = (s: T) => {
    const n = Number(s?.ct);
    return Number.isFinite(n) ? n : Infinity;
  };
  return items.reduce(
    (oldest, s) => (ctNum(s) < ctNum(oldest) ? s : oldest),
    items[0]
  );
}

/** When a Denmark parent is scheduled, stamp the same `schedule` on multi-publish child rows (UI only). */
function applyParentScheduleToMultiPublishChildren<
  T extends {
    section?: number;
    schedule?: number;
    status?: number;
    job_type?: string;
    multi_publish_targets?: { section?: number }[];
  },
>(items: T[]): T[] {
  const childSectionToSchedule = new Map<number, number>();
  for (const parent of items) {
    if (parent.job_type !== "denmark") continue;
    if (parent.status !== 10) continue;
    if (parent.schedule == null || Number(parent.schedule) <= Date.now()) {
      continue;
    }
    const targets = parent.multi_publish_targets;
    if (!Array.isArray(targets) || targets.length === 0) continue;
    const pSched = Number(parent.schedule);
    const pSection = Number(parent.section);
    for (const t of targets) {
      const cs = Number(t.section);
      if (Number.isNaN(cs) || cs === pSection) continue;
      childSectionToSchedule.set(cs, pSched);
    }
  }
  if (childSectionToSchedule.size === 0) return items;
  return items.map((item) => {
    if (item.job_type !== "denmark" || item.status !== 10) return item;
    const inherited = childSectionToSchedule.get(Number(item.section));
    if (inherited == null) return item;
    return { ...item, schedule: inherited };
  });
}

interface UseDenmarkPublishProps {
  formik: FormikProps<MediaDetailsForm>;
  currentMediaDetailsForEdit: MediaModel;
  hosts?: Host[];
  hostSubsection?: HostSubSection[];
  defaultTeam?: defaultTeamModel;
  onHasChanges?: (hasChanges: boolean) => void;
  /** Called when a Denmark chip is removed, so the form can flag itself dirty. */
  onDenmarkDirty?: () => void;
  selectedArticles?: any[];
  setSelectedArticles?: (articles: any[]) => void;

  getGlobalFavSectionList: (params: { host: number }) => Promise<unknown>;
}

interface UseDenmarkPublishReturn {
  // State
  currentSelectedSection: DenmarkPublishInterface[];
  editedSections: DenmarkPublishInterface[];
  selectedHost: string;
  selectedArticleHost: string;
  selectedSections: DenmarkPublishInterface[];
  selectedArticleSections: DenmarkPublishInterface[];
  showDenmarkRemovalAlert: boolean;
  pendingVideoshowRemovalId: number | null;

  // Setters
  setCurrentSelectedSection: React.Dispatch<
    React.SetStateAction<DenmarkPublishInterface[]>
  >;
  setSelectedHost: React.Dispatch<React.SetStateAction<string>>;
  setSelectedArticleHost: React.Dispatch<React.SetStateAction<string>>;
  setSelectedSections: React.Dispatch<
    React.SetStateAction<DenmarkPublishInterface[]>
  >;
  setSelectedArticleSections: React.Dispatch<
    React.SetStateAction<DenmarkPublishInterface[]>
  >;
  setShowDenmarkRemovalAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingVideoshowRemovalId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  setEditedSections: React.Dispatch<
    React.SetStateAction<DenmarkPublishInterface[]>
  >;

  // Handlers
  handlehostChange: (
    newSelectedHost: any,
    source?: "effect" | "select"
  ) => void;
  handleArticleHostChange: (newSelectedHost: any) => void;
  handleDenmarkchange: (section: any, actionMeta: any) => void;
  handleArticleDenmarkchange: (section: any, actionMeta: any) => void;
  handleAddDenmarkEmbedChip: (article: any, type: "lead" | "embed") => void;
  handleCancelOrDelete: (id: number, type: string) => void;
  handleConfirmDenmarkRemoval: () => void;
  queueDenmarkDelete: (
    item: { id?: string | number; section?: number } | string | number
  ) => void;
  /** Fire all queued Denmark deletes (call from the form's save success handler). */
  flushDenmarkDeletes: (dispatch: any) => void;
  createDenmarkEmbedSocialItem: (
    article: any,
    uploadType?: "lead" | "embed"
  ) => any;
  resetDenmarkState: () => void;
}

/**
 * Custom hook to manage Denmark publish logic
 * Extracted from MediaDetailsForm to improve code organization and reusability
 */
export const useDenmarkPublish = ({
  formik,
  currentMediaDetailsForEdit,
  hosts,
  hostSubsection,
  defaultTeam,
  onHasChanges,
  onDenmarkDirty,
  selectedArticles,
  setSelectedArticles,
  getGlobalFavSectionList,
}: UseDenmarkPublishProps): UseDenmarkPublishReturn => {
  const dispatch = useDispatch();

  // State
  const [currentSelectedSection, setCurrentSelectedSection] = useState<
    DenmarkPublishInterface[]
  >([]);
  const [editedSections, setEditedSections] = useState<
    DenmarkPublishInterface[]
  >([]);
  const [selectedHost, setSelectedHost] = useState<string>("");
  const [selectedArticleHost, setSelectedArticleHost] = useState<string>("");
  const [selectedSections, setSelectedSections] = useState<
    DenmarkPublishInterface[]
  >([]);
  const [selectedArticleSections, setSelectedArticleSections] = useState<
    DenmarkPublishInterface[]
  >([]);
  const [showDenmarkRemovalAlert, setShowDenmarkRemovalAlert] =
    useState<boolean>(false);
  const [pendingVideoshowRemovalId, setPendingVideoshowRemovalId] = useState<
    number | null
  >(null);

  // Refs for tracking user interactions
  const userSelectedHostRef = useRef(false);
  const userSelectedArticleHostRef = useRef(false);
  const lastHostChangeSourceRef = useRef<"effect" | "select" | undefined>(
    undefined
  );
  const favSeqRef = useRef(0);
  const pendingDenmarkDeletesRef = useRef<string[]>([]);

  /**
   * Creates a Denmark embed social item from an article
   */
  const createDenmarkEmbedSocialItem = useCallback(
    (article: any, uploadType: "lead" | "embed" = "lead") => ({
      id: article.id,
      section: article.id,
      channel_name: article.channel_name || article.title,
      host: parseInt(selectedArticleHost, 10),
      job_type: "denmark_embed",
      upload_type: uploadType,
      status: 100,
      title: article.title,
      desc: article.description || article.title,
      media_id: currentMediaDetailsForEdit?.id,
      platform: "denmark",
      ct: Date.now(),
      mt: Date.now(),
    }),
    [selectedArticleHost, currentMediaDetailsForEdit?.id]
  );

  /**
   * Effect: Initialize hosts — prefer first published Denmark host, else team default / first host
   */
  useEffect(() => {
    if (!hosts || hosts.length === 0) {
      return;
    }

    const publishedDenmarkHostsOrdered: number[] = [];
    const seenHostIds = new Set<number>();
    for (const s of currentMediaDetailsForEdit?.socials ?? []) {
      if (s.job_type !== "denmark" || s.status === 100) continue;
      const h = Number(s.host);
      if (!Number.isFinite(h) || seenHostIds.has(h)) continue;
      seenHostIds.add(h);
      publishedDenmarkHostsOrdered.push(h);
    }

    const fallbackHost = defaultTeam?.settings?.host ?? hosts[0]?.id ?? "";
    const defaultHost =
      publishedDenmarkHostsOrdered.length > 0
        ? String(publishedDenmarkHostsOrdered[0])
        : String(fallbackHost ?? "");

    if (
      !userSelectedHostRef.current ||
      lastHostChangeSourceRef.current === "effect"
    ) {
      if (defaultHost !== selectedHost) {
        setSelectedHost(defaultHost);
      }
    }

    if (
      !userSelectedArticleHostRef.current ||
      lastHostChangeSourceRef.current === "effect"
    ) {
      if (defaultHost !== selectedArticleHost) {
        setSelectedArticleHost(defaultHost);
      }
    }
  }, [
    hosts,
    selectedHost,
    selectedArticleHost,
    defaultTeam,
    currentMediaDetailsForEdit?.socials,
  ]);
  useEffect(() => {
    if (!selectedHost) return;
    const seq = ++favSeqRef.current;
    Promise.resolve().then(() => {
      if (favSeqRef.current !== seq) return;
      getGlobalFavSectionList({ host: Number(selectedHost) });
    });
  }, [selectedHost, getGlobalFavSectionList]);

  /**
   * Effect: Initialize sections from currentMediaDetailsForEdit.socials
   */
  useEffect(() => {
    if (currentMediaDetailsForEdit?.socials) {
      const isQueuedForDelete = (item: any) =>
        pendingDenmarkDeletesRef.current.includes(String(item?.id));
      const publishedItems = applyParentScheduleToMultiPublishChildren(
        currentMediaDetailsForEdit.socials
          .filter(
            (item) =>
              item.job_type === "denmark" &&
              item.status !== 100 &&
              !isQueuedForDelete(item)
          )
          .map((item) => ({
            ...item,
            id: item.id,
            section: item.section || 0,
            channel_details: item.channel_name || "",
            folder_path: item.folder_path || "",
          }))
      );
      const existingUnpublishedItems = selectedSections.filter(
        (item) =>
          item.status === 100 &&
          item.job_type === "denmark" &&
          !publishedItems.some(
            (published) => published.section === item.section
          )
      );
      setSelectedSections([...existingUnpublishedItems, ...publishedItems]);

      const publishedArticleItems = currentMediaDetailsForEdit.socials
        .filter(
          (item) => item.job_type === "denmark_embed" && item.status !== 100
        )
        .map((item) => ({
          ...item,
          id: item.id,
          section: item.section || 0,
          channel_details: item.channel_name || "",
          folder_path: item.folder_path || "",
        }));
      setSelectedArticleSections(publishedArticleItems);
    }
  }, [currentMediaDetailsForEdit?.socials]);

  /**
   * Handles host change for video publish
   */
  const handlehostChange = useCallback(
    (newSelectedHost: any, source?: "effect" | "select") => {
      lastHostChangeSourceRef.current = source;
      // Always mark these as true so default-host effects stop once any host is resolved
      userSelectedHostRef.current = true;
      userSelectedArticleHostRef.current = true;

      setSelectedHost(newSelectedHost.value);
      setSelectedArticleHost(newSelectedHost.value);
      if (hostSubsection && hostSubsection?.length > 0) {
        dispatch(setHostSubsection({ isLoading: true, list: [] }) as any);
      }
    },
    [hostSubsection, dispatch]
  );

  /**
   * Handles host change for article publish
   */
  const handleArticleHostChange = useCallback((newSelectedHost: any) => {
    userSelectedArticleHostRef.current = true;
    setSelectedArticleHost(newSelectedHost.value);
  }, []);

  /**
   * Handles Denmark section change for video publish
   */

  // this method is working fine as i expected so don't check this method #codex
  const handleDenmarkchange = useCallback(
    (section: any, actionMeta: any) => {
      // When called from DenmarkEditContent chip edits, update editedSections

      if (actionMeta?.action === "update-option") {
        const sectionId = section?.section ?? section?.id ?? section?.value;
        const resolveId = (item: any) =>
          item?.section ?? item?.id ?? item?.value;
        setEditedSections((prev) => {
          const exists = prev.some(
            (item) => Number(resolveId(item)) === Number(sectionId)
          );
          if (exists) {
            return prev.map((item) =>
              Number(resolveId(item)) === Number(sectionId)
                ? { ...item, ...section }
                : item
            );
          }
          const newEntry: DenmarkPublishInterface = {
            section: Number(sectionId),
            channel_name:
              section.channel_name ??
              section.label?.split("|")[1]?.trim() ??
              "",
            host: section.host ?? parseInt(selectedHost, 10),
            folder_path: section.folder_path ?? section.path ?? "",
            job_type: "denmark",
            upload_type: "api",
            media_id: currentMediaDetailsForEdit.id,
            platform: "denmark",
            tags: section.tags ?? currentMediaDetailsForEdit.tags ?? [],
            status: section.status ?? 100,
            id: section.id ?? section.value ?? Number(sectionId),
            ct: section.ct ?? 0,
            mt: section.mt ?? 0,
            title: section.title ?? currentMediaDetailsForEdit.title,
            desc: section.desc ?? currentMediaDetailsForEdit.desc,
            title_for_url:
              section?.title_for_url || currentMediaDetailsForEdit.title_url,
            schedule_after: section?.schedule_after,
            taxonomy: section?.taxonomy || [],
            schedule_type: section?.schedule_type,
            seotags: section?.seotags,
            privacy: section?.privacy ?? DENMARK_STATUS.ACTIVE,
            activationStatus: section?.activationStatus,
            multi_publish_targets: section?.multi_publish_targets || [],

            ...section,
          };
          return [...prev, newEntry];
        });
        return;
      }

      if (onHasChanges) {
        onHasChanges(true);
      }

      const newSectionId = parseInt(section.label.split("|")[0].trim(), 10);
      const newChannelName = section.label.split("|")[1].trim();

      // Duplicate guard
      const sectionExists = selectedSections.some(
        (item) => item.section === newSectionId
      );
      const sectionExistsInCurrent = currentSelectedSection.some(
        (item) => item.section === newSectionId
      );
      if (sectionExists || sectionExistsInCurrent) {
        return;
      }

      // --- Parent / child determination ---
      // "Published" = saved to backend (status !== 100), job_type === "denmark"
      const publishedDenmarkItems = (
        currentMediaDetailsForEdit?.socials || []
      ).filter((s) => s.job_type === "denmark" && s.status !== 100);
      const oldestPublishedDenmarkByCt = pickOldestDenmarkSocialByCt(
        publishedDenmarkItems
      );
      const publishedDenmarkCount = publishedDenmarkItems.length;

      // Top-level draft items (those that are not already children)
      const draftParentItems = currentSelectedSection.filter(
        (item) => item.job_type === "denmark" && !item._parentSectionId
      );

      const totalParentCount = publishedDenmarkCount + draftParentItems.length;

      const newSectionBase: DenmarkPublishInterface = {
        section: newSectionId,
        channel_name: newChannelName,
        host: section.host ?? parseInt(selectedHost, 10),
        folder_path: section.path || "",
        job_type: "denmark",
        upload_type: "api",
        media_id: currentMediaDetailsForEdit.id,
        platform: "denmark",
        tags: currentMediaDetailsForEdit.tags || [],
        status: 100,
        id: section.value,
        ct: 0,
        mt: 0,
        title: currentMediaDetailsForEdit.title,
        desc: currentMediaDetailsForEdit.desc,
        title_for_url: currentMediaDetailsForEdit.title_url,
        privacy:
          section.privacy ?? section.activationStatus ?? DENMARK_STATUS.ACTIVE,
      };

      let updatedDenmaykPublishes: DenmarkPublishInterface[];

      if (totalParentCount === 0) {
        // First section ever → becomes the parent
        updatedDenmaykPublishes = [newSectionBase, ...currentSelectedSection];
      } else if (totalParentCount === 1) {
        // There is already one parent (published or draft) → new section is a child
        const childTarget = {
          section: newSectionId,
          parent_id: newSectionId,
          channel_name: newChannelName,
          host: section.host ?? parseInt(selectedHost, 10),
          folder_path: section.path || "",
          open_story_at: "PUBLISH_SECTION" as const,
          open_newly_in_browser: false,
          isOverRideLinkRequired: true,
          contentPublishType: "MULTIPUBLISH" as const,
        };

        if (draftParentItems.length === 1) {
          // Parent is in currentSelectedSection → update its multi_publish_targets in-place
          const draftParent = draftParentItems[0];
          const withUpdatedParent = currentSelectedSection.map((item) =>
            item.section === draftParent.section
              ? {
                  ...item,
                  multi_publish_targets: [
                    ...(item.multi_publish_targets || []),
                    childTarget,
                  ],
                }
              : item
          );
          // Prepend the child chip (marked so the submit knows it belongs to a parent)
          updatedDenmaykPublishes = [
            { ...newSectionBase, _parentSectionId: draftParent.section },
            ...withUpdatedParent,
          ];
        } else {
          // Parent is already published (in selectedSections / socials)
          const publishedParent = oldestPublishedDenmarkByCt;
          if (!publishedParent) return;
          updatedDenmaykPublishes = [
            {
              ...newSectionBase,
              _parentSectionId: publishedParent.section,
            },
            ...currentSelectedSection,
          ];
        }
      } else {
        // totalParentCount >= 2 → add as child of the oldest published parent by ct
        const oldestPublishedParent = oldestPublishedDenmarkByCt;
        if (!oldestPublishedParent) return;
        updatedDenmaykPublishes = [
          {
            ...newSectionBase,
            _parentSectionId: oldestPublishedParent.section,
          },
          ...currentSelectedSection,
        ];
      }

      setCurrentSelectedSection(updatedDenmaykPublishes);
      try {
        const currentDenmark = formik.values.denmark || [];
        const existingEmbedItems = currentDenmark.filter(
          (d: any) => d?.job_type === "denmark_embed"
        );
        const updatedDenmark = [
          ...updatedDenmaykPublishes,
          ...existingEmbedItems,
        ];
        formik.setFieldValue("denmark", updatedDenmark, false);
        formik.setFieldValue(
          "is_denmark",
          updatedDenmaykPublishes.length > 0,
          false
        );
      } catch (e) {
        console.error("[Denmark] Failed syncing to formik", e);
      }
    },
    [
      onHasChanges,
      selectedSections,
      currentSelectedSection,
      selectedHost,
      currentMediaDetailsForEdit,
      formik,
    ]
  );

  /**
   * Handles Denmark section change for article publish (placeholder)
   */
  const handleArticleDenmarkchange = useCallback(
    (section: any, actionMeta: any) => {
      if (onHasChanges) {
        onHasChanges(true);
      }
    },
    [onHasChanges]
  );

  /**
   * Handles adding Denmark embed chip from article
   */
  const handleAddDenmarkEmbedChip = useCallback(
    (article: any, type: "lead" | "embed") => {
      if (onHasChanges) {
        onHasChanges(true);
      }
      const currentDenmark = formik.values.denmark || [];
      const socialItem = createDenmarkEmbedSocialItem(article, type);
      const newDenmarkEmbed = {
        ...socialItem,
        section: Number(article.msid) || Number(article.id),
        id: article.id || article.msid,
        title: currentMediaDetailsForEdit?.title || article.title || "",
        desc: currentMediaDetailsForEdit?.desc || article.description || "",
      };
      const exists = currentDenmark.some(
        (d: any) =>
          d?.job_type === "denmark_embed" &&
          (d?.section === newDenmarkEmbed.section ||
            d?.id === newDenmarkEmbed.id)
      );
      if (!exists) {
        formik.setFieldValue(
          "denmark",
          [...currentDenmark, newDenmarkEmbed],
          false
        );
      }
    },
    [
      onHasChanges,
      formik,
      createDenmarkEmbedSocialItem,
      currentMediaDetailsForEdit,
    ]
  );

  /**
   * Handles canceling or deleting a Denmark publish item
   */
  const handleCancelOrDelete = useCallback(
    (id: number, type: string) => {
      // Remove from editedSections so the sync useEffect in the modal doesn't re-add it
      setEditedSections((prev) =>
        prev.filter((item) => item.section !== id && item.id !== id)
      );
      const currentDenmark = formik.values.denmark || [];
      const isDenmarkEmbedItem = Array.isArray(currentDenmark)
        ? currentDenmark.some(
            (d: any) =>
              (d?.section === id || d?.id === id) &&
              d?.job_type === "denmark_embed"
          )
        : false;
      if (!isDenmarkEmbedItem) {
        const hasPublishedDenmark = currentMediaDetailsForEdit?.socials?.some(
          (social: any) => social.job_type === "denmark" && social.status === 10
        );
        const temporaryVideoshowChips = currentSelectedSection.filter(
          (item) => item.job_type === "denmark" && item.status === 100
        );
        const temporaryArticleshowChips = Array.isArray(formik.values.denmark)
          ? formik.values.denmark.filter(
              (d: any) => d?.job_type === "denmark_embed" && d?.status === 100
            )
          : [];
        const isLastVideoshowChip =
          temporaryVideoshowChips.length === 1 &&
          temporaryVideoshowChips[0].section === id;
        if (
          !hasPublishedDenmark &&
          temporaryVideoshowChips.length > 0 &&
          temporaryArticleshowChips.length > 0 &&
          isLastVideoshowChip
        ) {
          setPendingVideoshowRemovalId(id);
          setShowDenmarkRemovalAlert(true);
          return;
        }
      }
      if (!isDenmarkEmbedItem) {
        setCurrentSelectedSection((prevSections) => {
          const itemBeingDeleted = prevSections.find(
            (item) => item.section === id
          );
          const isParent =
            itemBeingDeleted && !itemBeingDeleted._parentSectionId;

          let updatedSections = prevSections.filter(
            (item) => item.section !== id
          );

          if (isParent) {
            // Deleting a parent → also remove its children chips
            updatedSections = updatedSections.filter(
              (item) => item._parentSectionId !== id
            );
          } else if (itemBeingDeleted?._parentSectionId) {
            // Deleting a child whose parent lives in currentSelectedSection
            // → remove the child from the parent's multi_publish_targets
            updatedSections = updatedSections.map((item) =>
              item.section === itemBeingDeleted._parentSectionId
                ? {
                    ...item,
                    multi_publish_targets: (
                      item.multi_publish_targets || []
                    ).filter((t: any) => Number(t.section) !== id),
                  }
                : item
            );
          }

          const updatedDenmark = currentDenmark.filter((d: any) => {
            if (d?.job_type !== "denmark") return true;
            if (d?.section === id || d?.id === id) return false;
            // Remove orphaned children when deleting a parent
            if (isParent && d?._parentSectionId === id) return false;
            return true;
          });
          formik.setFieldValue("denmark", updatedDenmark, false);
          formik.setFieldValue("is_denmark", updatedDenmark.length > 0, false);
          return updatedSections;
        });
      } else {
        const denmarkItem = Array.isArray(currentDenmark)
          ? currentDenmark.find(
              (d: any) =>
                (d?.section === id || d?.id === id) &&
                d?.job_type === "denmark_embed"
            )
          : null;
        if (denmarkItem) {
          const updatedDenmark = currentDenmark.filter(
            (d: any) =>
              !(
                (d?.section === id || d?.id === id) &&
                d?.job_type === "denmark_embed"
              )
          );
          formik.setFieldValue("denmark", updatedDenmark, false);
          if (selectedArticles && setSelectedArticles) {
            const articleIdToRemove =
              denmarkItem.id?.toString() ||
              denmarkItem.section?.toString() ||
              id.toString();
            const updatedArticles = selectedArticles.filter(
              (a) =>
                a.id?.toString() !== articleIdToRemove &&
                a.msid?.toString() !== articleIdToRemove
            );
            if (updatedArticles.length !== selectedArticles.length) {
              setSelectedArticles(updatedArticles);
            }
          }
        }
      }
      onDenmarkDirty?.();
    },
    [
      formik,
      currentMediaDetailsForEdit,
      currentSelectedSection,
      selectedArticles,
      setSelectedArticles,
      onDenmarkDirty,
    ]
  );

  /**
   * Handles confirming Denmark removal after alert
   */
  const handleConfirmDenmarkRemoval = useCallback(() => {
    if (pendingVideoshowRemovalId === null) return;

    const currentDenmark = formik.values.denmark || [];
    setCurrentSelectedSection((prevSections) => {
      return prevSections.filter(
        (item) => item.section !== pendingVideoshowRemovalId
      );
    });
    let updatedDenmark = currentDenmark.filter(
      (d: any) =>
        !(
          (d?.section === pendingVideoshowRemovalId ||
            d?.id === pendingVideoshowRemovalId) &&
          d?.job_type === "denmark"
        )
    );
    updatedDenmark = updatedDenmark.filter(
      (d: any) => !(d?.job_type === "denmark_embed" && d?.status === 100)
    );
    formik.setFieldValue("denmark", updatedDenmark, false);
    formik.setFieldValue(
      "is_denmark",
      updatedDenmark.some((d: any) => d?.job_type === "denmark"),
      false
    );
    if (selectedArticles && setSelectedArticles) {
      const articleIdsToRemove = new Set(
        currentDenmark
          .filter(
            (d: any) => d?.job_type === "denmark_embed" && d?.status === 100
          )
          .map((d: any) => d?.id?.toString() || d?.section?.toString())
      );
      const updatedArticles = selectedArticles.filter(
        (a) =>
          !articleIdsToRemove.has(a.id?.toString()) &&
          !articleIdsToRemove.has(a.msid?.toString())
      );
      if (updatedArticles.length !== selectedArticles.length) {
        setSelectedArticles(updatedArticles);
      }
    }
    setShowDenmarkRemovalAlert(false);
    setPendingVideoshowRemovalId(null);
  }, [
    pendingVideoshowRemovalId,
    formik,
    selectedArticles,
    setSelectedArticles,
  ]);

  /**
   * Resets Denmark state (useful for undo changes)
   */
  const resetDenmarkState = useCallback(() => {
    setCurrentSelectedSection([]);
    setSelectedSections([]);
  }, []);

  const queueDenmarkDelete = useCallback(
    (item: { id?: string | number; section?: number } | string | number) => {
      const rawId = typeof item === "object" && item !== null ? item.id : item;
      const section =
        typeof item === "object" && item !== null ? item.section : undefined;
      const id = rawId != null ? String(rawId) : "";
      if (id && !pendingDenmarkDeletesRef.current.includes(id)) {
        pendingDenmarkDeletesRef.current.push(id);
      }

      const matches = (s: any) =>
        (section != null && s?.section === section) ||
        (rawId != null && (s?.id === rawId || String(s?.id) === id));

      setSelectedSections((prev) => prev.filter((s) => !matches(s)));
      setCurrentSelectedSection((prev) => prev.filter((s) => !matches(s)));
      setEditedSections((prev) => prev.filter((s) => !matches(s)));

      const currentDenmark = formik.values.denmark || [];
      const updatedDenmark = Array.isArray(currentDenmark)
        ? currentDenmark.filter((d: any) => !matches(d))
        : currentDenmark;
      formik.setFieldValue("denmark", updatedDenmark, false);
      formik.setFieldValue(
        "is_denmark",
        Array.isArray(updatedDenmark) && updatedDenmark.length > 0,
        false
      );
      onDenmarkDirty?.();
    },
    [formik, onDenmarkDirty]
  );

  const flushDenmarkDeletes = useCallback((dispatchFn: any) => {
    if (pendingDenmarkDeletesRef.current.length === 0) return;
    pendingDenmarkDeletesRef.current.forEach((id) => {
      if (id) dispatchFn(deleteDenmarkPublish({ id }) as any);
    });
    pendingDenmarkDeletesRef.current = [];
  }, []);

  return {
    // State
    currentSelectedSection,
    editedSections,
    selectedHost,
    selectedArticleHost,
    selectedSections,
    selectedArticleSections,
    showDenmarkRemovalAlert,
    pendingVideoshowRemovalId,

    // Setters
    setCurrentSelectedSection,
    setSelectedHost,
    setSelectedArticleHost,
    setSelectedSections,
    setSelectedArticleSections,
    setShowDenmarkRemovalAlert,
    setPendingVideoshowRemovalId,
    setEditedSections,

    // Handlers
    handlehostChange,
    handleArticleHostChange,
    handleDenmarkchange,
    handleArticleDenmarkchange,
    handleAddDenmarkEmbedChip,
    handleCancelOrDelete,
    handleConfirmDenmarkRemoval,
    queueDenmarkDelete,
    flushDenmarkDeletes,
    createDenmarkEmbedSocialItem,
    resetDenmarkState,
  };
};
